import { useState } from 'react';
import { Badge, Card, Nav } from 'react-bootstrap';
import { ArrowUpRight, Check, Moon, Plus, ShieldCheck, Star, Sun } from 'lucide-react';
import type {
  SkinAnalysisPredictValidationConcernMatch,
  SkinAnalysisPredictValidationExpectedChanges,
  SkinAnalysisPredictValidationLegacyConcentration,
  SkinAnalysisPredictValidationLegacyRoutineCards,
  SkinAnalysisPredictValidationRoutineCard,
  SkinAnalysisPredictValidationRoutineReveal,
  SkinAnalysisPredictValidationStructuredConcentration,
} from '@/models/SkinAnalysisPredictValidation';

type ScheduleFilter = 'ALL' | 'AM' | 'PM';

interface ValidationRoutineRevealProps {
  reveal: SkinAnalysisPredictValidationRoutineReveal;
}

interface ValidationConcernMatchesProps {
  concernMatches: SkinAnalysisPredictValidationConcernMatch[];
}

interface ValidationRoutineCardsProps {
  routineCards: SkinAnalysisPredictValidationRoutineCard[] | SkinAnalysisPredictValidationLegacyRoutineCards;
}

interface ValidationMilestonesProps {
  expectedChanges: SkinAnalysisPredictValidationExpectedChanges | null;
}

const TIMING_PERIOD_LABELS = ['2–4 WEEKS', '4–8 WEEKS', '8–12 WEEKS'] as const;

/** Normalizes the previous schedule object during the Admin-first rollout; new responses pass through unchanged. */
function normalizeRoutineCards(
  routineCards: SkinAnalysisPredictValidationRoutineCard[] | SkinAnalysisPredictValidationLegacyRoutineCards,
): SkinAnalysisPredictValidationRoutineCard[] {
  if (Array.isArray(routineCards)) {
    return routineCards;
  }
  const cardsByProductStep = new Map<string, SkinAnalysisPredictValidationRoutineCard>();
  for (const schedule of ['AM', 'PM'] as const) {
    for (const legacyCard of routineCards[schedule]) {
      const { schedule: legacySchedule, stepNumber, ...card } = legacyCard;
      const cardKey = `${card.stepKey}:${card.product.productId}`;
      const existing = cardsByProductStep.get(cardKey);
      if (existing) {
        if (!existing.scheduleTags.includes(legacySchedule)) {
          existing.scheduleTags.push(legacySchedule);
        }
        existing.positions.push({ schedule: legacySchedule, stepNumber });
        continue;
      }
      cardsByProductStep.set(cardKey, {
        ...card,
        scheduleTags: [legacySchedule],
        positions: [{ schedule: legacySchedule, stepNumber }],
      });
    }
  }
  return [...cardsByProductStep.values()];
}

function concentrationBasisLabel(basis: SkinAnalysisPredictValidationStructuredConcentration['basis']): string {
  switch (basis) {
    case 'source_claim':
      return 'Source claim';
    case 'inci_order_anchor':
      return 'INCI order anchor';
    case 'regulatory_limit':
      return 'Regulatory limit';
  }
}

function isStructuredConcentration(
  evidence: SkinAnalysisPredictValidationRoutineCard['concentration'],
): evidence is SkinAnalysisPredictValidationStructuredConcentration {
  return Boolean(evidence && 'qualifier' in evidence && 'displayText' in evidence);
}

function StructuredConcentrationEvidence({
  evidence,
}: {
  evidence: SkinAnalysisPredictValidationStructuredConcentration;
}) {
  const { referencePoint } = evidence;
  const ingredientLabel = evidence.ingredientFamily
    ? `${evidence.ingredientFamily} · ${evidence.ingredientName}`
    : evidence.ingredientName;
  const valueLabel = evidence.basis === 'regulatory_limit' ? 'Regulatory limit' : 'Product concentration';

  return (
    <section
      className={`client-concentration-evidence mb-3${referencePoint ? '' : ' client-concentration-evidence-compact'}`}
      aria-label={`${ingredientLabel} concentration evidence`}
      data-qualifier={evidence.qualifier}
    >
      <header className="client-concentration-heading">
        <div>
          <span>Ingredient evidence</span>
          <strong>{ingredientLabel}</strong>
        </div>
      </header>
      <div className="client-concentration-values">
        <div className="client-concentration-product-value">
          <span>{valueLabel}</span>
          <strong>{evidence.displayText}</strong>
        </div>
        {referencePoint && (
          <>
            <span className="client-concentration-reference-marker" aria-hidden="true" />
            <div className="client-concentration-reference-value">
              <span>Reference point</span>
              <strong>{referencePoint.percent}%</strong>
            </div>
          </>
        )}
      </div>
      {referencePoint && evidence.comparisonText && (
        <p className="client-concentration-comparison">{evidence.comparisonText}</p>
      )}
      {referencePoint && (
        <p className="client-concentration-caption">
          <span aria-hidden="true" />
          Reference point · {referencePoint.label}
        </p>
      )}
      <details className="client-concentration-details">
        <summary>Evidence details</summary>
        <dl>
          <div>
            <dt>Method</dt>
            <dd>{concentrationBasisLabel(evidence.basis)}</dd>
          </div>
          <div>
            <dt>Confidence</dt>
            <dd>{evidence.confidence}</dd>
          </div>
          <div>
            <dt>Product source</dt>
            <dd>{evidence.sourceReference}</dd>
          </div>
          <div>
            <dt>Product policy</dt>
            <dd>{evidence.policyVersion}</dd>
          </div>
          {referencePoint && (
            <>
              <div>
                <dt>Reference source</dt>
                <dd>{referencePoint.sourceReference}</dd>
              </div>
              <div>
                <dt>Reference policy</dt>
                <dd>{referencePoint.policyVersion}</dd>
              </div>
            </>
          )}
        </dl>
      </details>
    </section>
  );
}

function LegacyConcentrationEvidence({ evidence }: { evidence: SkinAnalysisPredictValidationLegacyConcentration }) {
  return (
    <div className="client-concentration client-concentration-legacy mb-3">
      <strong>
        {evidence.ingredientName} {evidence.value}
      </strong>
      <span>{evidence.sourceReference}</span>
    </div>
  );
}

export function ValidationRoutineReveal({ reveal }: ValidationRoutineRevealProps) {
  return (
    <section className="client-routine-hero">
      <p className="client-eyebrow">Skin-matched routine</p>
      <h2>{reveal.routineName}</h2>
      <p>{reveal.routineSummary}</p>
      {reveal.topConcerns.length > 0 && (
        <ol className="client-concern-list">
          {reveal.topConcerns.map((concern) => (
            <li key={concern.metricKey}>
              <div className="client-concern-heading">
                <strong>{concern.label}</strong>
                <span>{concern.score}/100</span>
              </div>
              <p>{concern.explanation}</p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

export function ValidationConcernMatches({ concernMatches }: ValidationConcernMatchesProps) {
  if (concernMatches.length === 0) {
    return null;
  }
  return (
    <Card className="client-routine-shell mb-3">
      <Card.Body className="p-4 p-md-5">
        <h3>Every concern, matched to an active</h3>
        <div className="client-concern-match-grid">
          {concernMatches.map((match) => (
            <article key={match.metricKey}>
              <div className="client-concern-heading">
                <strong>{match.label}</strong>
                <Badge bg="light" text="primary" className="client-active-badge">
                  {match.ingredient.name}
                </Badge>
              </div>
              <p>{match.explanation}</p>
              <div className="client-matched-products">
                {match.matchedProducts.map((product) => (
                  <span key={`${product.schedule}-${product.stepKey}-${product.productId}`}>
                    {product.schedule} · Step {product.stepNumber} · {product.productName}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
}

function RoutineCard({
  card,
  activeSchedule,
}: {
  card: SkinAnalysisPredictValidationRoutineCard;
  activeSchedule: ScheduleFilter;
}) {
  const review = card.reviewHighlight;
  const verifiedEvidence = card.evidenceBullets.find((bullet) => bullet.kind === 'verified');
  const fitEvidence = card.evidenceBullets.find((bullet) => bullet.kind === 'fit');
  const hasScreeningCopy = Boolean(card.screening.passedText || card.screening.excludedText);
  const visiblePositions =
    activeSchedule === 'ALL'
      ? card.positions
      : card.positions.filter((position) => position.schedule === activeSchedule);
  const solePosition = visiblePositions.length === 1 ? visiblePositions[0] : undefined;
  const stepLabel = solePosition
    ? `${solePosition.stepNumber}. ${card.stepName}`
    : `${visiblePositions.map((position) => `${position.schedule} ${position.stepNumber}`).join(' · ')} · ${card.stepName}`;
  return (
    <Card className="client-routine-card mb-3">
      <Card.Body className="p-4">
        <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
          <Badge bg="light" text="dark" className="client-step-badge">
            {stepLabel}
          </Badge>
          {card.scheduleTags.map((schedule) => (
            <Badge bg="light" text="primary" className="client-schedule-badge" key={schedule}>
              {schedule === 'AM' ? <Sun size={14} /> : <Moon size={14} />}
              {schedule === 'AM' ? 'Morning' : 'Night'}
            </Badge>
          ))}
          {card.isKeyRoutine && (
            <Badge bg="primary" className="client-key-badge">
              Key routine
            </Badge>
          )}
        </div>
        <div className="client-product-row">
          {card.product.imageUrl && (
            <img className="client-product-image" src={card.product.imageUrl} alt={card.product.productName} />
          )}
          <div>
            {card.product.brandName && <div className="client-product-brand">{card.product.brandName}</div>}
            <div className="client-product-name">
              {card.product.pdpUrl ? (
                <a href={card.product.pdpUrl} target="_blank" rel="noreferrer">
                  {card.product.productName}
                </a>
              ) : (
                card.product.productName
              )}
            </div>
            {card.product.rating !== null && (
              <div className="client-product-rating">
                <Star size={14} aria-hidden="true" />
                {card.product.rating}
                {card.product.reviewCount !== null && <span> · {card.product.reviewCount} reviews</span>}
              </div>
            )}
          </div>
        </div>
        <p className="client-product-description mb-3">{card.summary}</p>
        {(verifiedEvidence || fitEvidence) && (
          <div className="client-evidence-panel mb-3">
            {verifiedEvidence && (
              <div className="client-evidence-row client-evidence-row-verified">
                <span className="client-evidence-marker" aria-hidden="true">
                  <Check size={16} />
                </span>
                <div>
                  <strong>Verified product evidence</strong>
                  <span>{verifiedEvidence.text}</span>
                </div>
              </div>
            )}
            {fitEvidence && (
              <div className="client-evidence-row client-evidence-row-fit">
                <span className="client-evidence-marker" aria-hidden="true">
                  <Plus size={16} />
                </span>
                <div>
                  <strong>Why it fits you</strong>
                  <span>{fitEvidence.text}</span>
                </div>
              </div>
            )}
          </div>
        )}
        {isStructuredConcentration(card.concentration) ? (
          <StructuredConcentrationEvidence evidence={card.concentration} />
        ) : (
          card.concentration && <LegacyConcentrationEvidence evidence={card.concentration} />
        )}
        {hasScreeningCopy && (
          <details className="client-screening">
            <summary>
              <ShieldCheck size={17} aria-hidden="true" /> Verified ingredients for your skin
            </summary>
            <div>
              {card.screening.excludedText && (
                <p className="client-screening-row client-screening-row-excluded">
                  <strong>Excluded</strong>
                  <span>{card.screening.excludedText}</span>
                </p>
              )}
              {card.screening.passedText && (
                <p className="client-screening-row client-screening-row-passed">
                  <strong>Passed</strong>
                  <span>{card.screening.passedText}</span>
                </p>
              )}
            </div>
          </details>
        )}
        {review && (
          <blockquote className="client-review">
            <p>{review.excerpt}</p>
            <footer>
              <span className="client-review-profile">{review.tags.join(' · ')}</span>
            </footer>
          </blockquote>
        )}
        {card.expertNote && <p className="client-expert-note">{card.expertNote}</p>}
      </Card.Body>
    </Card>
  );
}

export function ValidationRoutineCards({ routineCards }: ValidationRoutineCardsProps) {
  const [activeSchedule, setActiveSchedule] = useState<ScheduleFilter>('ALL');
  const normalizedCards = normalizeRoutineCards(routineCards);
  const scheduleCount = (schedule: 'AM' | 'PM') =>
    normalizedCards.reduce(
      (count, card) => count + card.positions.filter((position) => position.schedule === schedule).length,
      0,
    );
  const morningCount = scheduleCount('AM');
  const nightCount = scheduleCount('PM');
  const visibleCards =
    activeSchedule === 'ALL'
      ? normalizedCards
      : normalizedCards
          .filter((card) => card.scheduleTags.includes(activeSchedule))
          .sort(
            (left, right) =>
              (left.positions.find((position) => position.schedule === activeSchedule)?.stepNumber ??
                Number.MAX_SAFE_INTEGER) -
              (right.positions.find((position) => position.schedule === activeSchedule)?.stepNumber ??
                Number.MAX_SAFE_INTEGER),
          );
  return (
    <Card className="client-routine-shell mb-3">
      <Card.Body className="p-4 p-md-5">
        <h3>Just follow the order</h3>
        <p className="client-routine-count mb-4">
          {morningCount} step{morningCount === 1 ? '' : 's'} in the morning, {nightCount} step
          {nightCount === 1 ? '' : 's'} at night.
        </p>
        <Nav
          variant="pills"
          className="client-schedule-tabs mb-4"
          activeKey={activeSchedule}
          onSelect={(key) => setActiveSchedule((key as ScheduleFilter) ?? 'ALL')}
        >
          <Nav.Item>
            <Nav.Link eventKey="ALL">All</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="AM">
              <Sun size={14} /> Morning
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="PM">
              <Moon size={14} /> Night
            </Nav.Link>
          </Nav.Item>
        </Nav>
        {visibleCards.map((card) => (
          <RoutineCard key={`${card.stepKey}-${card.product.productId}`} card={card} activeSchedule={activeSchedule} />
        ))}
      </Card.Body>
    </Card>
  );
}

export function ValidationMilestones({ expectedChanges }: ValidationMilestonesProps) {
  if (!expectedChanges || expectedChanges.entries.length !== TIMING_PERIOD_LABELS.length) {
    return null;
  }
  return (
    <section className="recommendation-client-section recommendation-changes-section">
      <div className="recommendation-client-section-heading">
        <div>
          <span className="recommendation-eyebrow">Routine outlook</span>
          <h2>When will you see a difference?</h2>
          <p>{expectedChanges.subtitle}</p>
        </div>
      </div>
      <div className="recommendation-change-timeline">
        {expectedChanges.entries.map((entry, index) => (
          <article key={`${index}-${entry.title}`}>
            <span>{TIMING_PERIOD_LABELS[index] ?? ''}</span>
            <div>
              <h3>{entry.title}</h3>
              <p>{entry.description}</p>
            </div>
            <ArrowUpRight size={18} aria-hidden="true" />
          </article>
        ))}
      </div>
      <p>Results vary from person to person. If you have a skin condition, speak to a dermatologist first.</p>
    </section>
  );
}
